// Single source of truth for order title display.
//
// ═══════════════════════════════════════════════════════════════════════════
// THE DISPLAYED IDENTIFIER IS `ORD-<orders.order_number>` — 2026-08-12
// ═══════════════════════════════════════════════════════════════════════════
// Product owner: *"lets just go with ORD-001, ORD-002 etc etc … as long as I
// can search for ORD-00190 and it shows me that order and ORD-00190 shows up on
// all pdfs, csv, excell, table views etc etc and PO number if it exists we're
// good to go"*.
//
// 🔑 **`order_number` is the PER-DEPARTMENT sequence, NOT the global `orders.id`.**
// Every department counts from 1, so Elstree's fourth order is `ORD-004`, not
// `ORD-2454`. The server allocates and stores it (`server/helpers/orderNumber.js`,
// `allocateOrderNumber`), a boot backfill fills legacy rows, and `SELECT o.*`
// carries it on every order payload. This module NEVER derives from `order.id`.
// Owner, on why not the production: *"production is currently a label there is
// nothing that actualy links two departments … Nothing, not even the identaical
// production name links them."*
//
// All viewers see `<po_number> · ORD-<order_number>` — or just `ORD-<order_number>`
// on an order that has no PO YET. Same identifier everywhere, so the title is
// consistent across roles. Regular users still can't EDIT the PO field
// (server-gated), they just see it.
//
// Keep notification messages, audit-log strings, and confirmation prompts as
// `ORD-<order_number>` too — they reach mixed audiences and need the one stable
// identifier a person quotes down a phone.

/**
 * `1` → `'ORD-001'`. THE client mirror of `formatOrderNumber` in
 * `server/helpers/orderNumber.js` — the two halves of this repo cannot import
 * from each other, so the copies are held identical by hand exactly as
 * `csvEscape` and `normalisePoNumber` are, and pinned by
 * `server/tests/regressions/order-number-client-parity.test.js`.
 *
 * 🪤 `orderNumber == null`, never `!orderNumber`: the sequence starts at 1 so a
 * zero never occurs, but the falsy test is the habit that loses `0` elsewhere in
 * this codebase and there is no reason to practise it here.
 *
 * Returns `null` (never `'ORD-NaN'`) for an unusable value, so a caller that
 * spreads it into a template renders nothing rather than a plausible-looking lie.
 * **`padStart(3, '0')` is load-bearing** — it is what makes a spreadsheet cell
 * byte-identical to the string on screen for orders 1–99.
 */
export function formatOrderNumber(orderNumber) {
  if (orderNumber == null) return null;
  const n = Number(orderNumber);
  if (!Number.isFinite(n)) return null;
  return `ORD-${String(n).padStart(3, '0')}`;
}

/**
 * THE displayed order number for an order object — `ORD-004`.
 *
 * Reads `order.order_number` and nothing else. Answers `''` for an order with no
 * number (an unsaved object, e.g. `OrderPage.js`'s `<h1>` before the fetch
 * resolves) so it can drop into JSX without rendering a sentinel.
 */
export function getOrderNumber(order) {
  if (!order) return '';
  return formatOrderNumber(order.order_number) || '';
}

/**
 * ⛔ **DEPRECATED FOR DISPLAY — 2026-08-12. DO NOT ADD A DISPLAY CALL SITE.**
 *
 * Returns `orders.reference_number` (`AURORA-ORD-120`), the production-prefixed
 * global string the owner has ruled is shown NOWHERE: *"we only show the true
 * order ID never a concatinated order ID and production like `AURORA-ORD-120`
 * anywhere JUST ORDER ID AND PO"*. It survives only as a SEARCH key
 * (`utils/orderSearch.js`) so paperwork already sent to a supplier still
 * resolves — being findable is not being shown. Kept, not deleted, because
 * `reference_number` is still a stored column and this is the one honest reader
 * of it left; every display caller moved to `getOrderNumber`.
 */
export function getOrderRef(order) {
  if (!order) return '';
  return order.reference_number || `ORD-${String(order.id).padStart(3, '0')}`;
}

// Uses the same separator-requiring test as `server/helpers/poNumber.js`, for
// the same reason: a value of `POWER-1` must NOT be treated as already-prefixed.
const ALREADY_PO_PREFIXED = /^PO[-\s]/i;

/**
 * THE ONE-LINE ORDER TITLE — `PO-0001 · ORD-004`, or `ORD-004` alone.
 *
 * The `{ isAdmin, isBuyer }` options arg is accepted and IGNORED, kept for
 * backwards compatibility with existing call sites — the title is
 * role-independent by ruling.
 *
 * 🔑 **PO FIRST, THEN THE ORDER NUMBER, joined by ` · `.** The order (PO on
 * line 1) matches the table's stacked PO cell (`OrderTableView.js`, *"Put PO on
 * top"*). The separator is `·` (U+00B7), not `-`: both halves carry hyphens of
 * their own (`PO-0001`, `ORD-004`), so a `-` join would read as one token.
 *
 * 🔴 **The order number is ALWAYS present; the PO joins it when there is one.**
 * `filter(Boolean)` keeps the two obligations independent — an order with only
 * one renders that one alone, never a dangling separator. No `PO:___`
 * placeholder: once the number is always present the card is never unidentified.
 *
 * `PO-147` renders as itself; a LEGACY unprefixed `147` still takes the `PO:`
 * label, because with no prefix in the data the label is the only thing saying
 * what the number is.
 */
export function getOrderTitle(order /* , _options = {} */) {
  if (!order) return '';
  const po = order.po_number
    ? (ALREADY_PO_PREFIXED.test(String(order.po_number)) ? String(order.po_number) : `PO:${order.po_number}`)
    : '';
  const number = getOrderNumber(order);
  return [po, number].filter(Boolean).join(' · ');
}
